import { bottomLevel, worldKeyForBuildingEntity, groupByWorldKey } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type {
  GeneratedIntermediateFoundationSubSystem,
  GeneratedFoundationSubSystem,
} from "building-types/foundations/foundationTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import uid from "common/utils/uid";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { getFoundationLevelIds } from "../elements/foundation/autoFoundationSystem";
import { applyFoundationOverlapResolution } from "../elements/foundation/foundationOverlapResolution";
import {
  buildFoundationSubSystemExtrusions,
  convertFoundationSubSystemToIntermediate,
  makeIntermediateFoundationSubSystem,
} from "../elements/foundation/foundationSubSystem";
import { shouldProcessFloorWithBoundaries } from "../generationTools/generateFloorHorizontalAssembliesTools";
import {
  entityHandleForSelectingReportingUnitFromEntityId,
  entityHandleForSelectingReportingUnitFromLevel,
} from "../generationTools/getInstanceIdFromLevel";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  levels,
  planarGraphs,
  foundationPaths,
  floorPaths,
  foundationHorizontalAssemblies,
  foundationHorizontalAssembliesPaths,
  floorBoundaries,
  datumAbstractions,
  optionChoices,
  entityDisabledByGroup,
  rooms,
  roomsByLevel,
  horizontalAssemblyFoundations: foundations,
  horizontalAssemblyFoundationSystems: foundationSystems,
  horizontalAssemblyFloorSystems: floorSystems,
  horizontalAssemblyFloors: floors,
  doc: {
    foundationComponents,
    framingComponents,
    slabComponents,
    sheathingComponents,
    optionSets,
    instanceOptionSets,
    componentInstances,
  },
  idToParents,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    { foundations, foundationSystems, floors, floorSystems },
    definitions,
    optionChoices,
  );
  return {
    levels,
    planarGraphs,
    foundationPaths,
    floorPaths,
    foundationHorizontalAssemblies,
    foundationHorizontalAssembliesPaths,
    floorBoundaries,
    framingComponents,
    slabComponents,
    sheathingComponents,
    foundationSettings: settings.foundation,
    foundationComponents,
    foundations,
    foundationSystems,
    floorSystems,
    floors,
    datumAbstractions,
    doorSettings: settings.door,
    optionChoices: resolvedOptionChoices,
    entityDisabledByGroup,
    rooms,
    roomsByLevel,
    idToParents,
    componentInstances,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  foundationSettings: "shallow",
  optionChoices: "shallow",
  idToParents: "shallow",
  componentInstances: "shallow",
  floors: "shallow",
  floorSystems: "shallow",
  foundations: "shallow",
  foundationSystems: "shallow",
});

function generateFoundationSystemsForWorlds(input: ReturnType<typeof select>): {
  foundations: Table<GeneratedFoundationSubSystem>;
  issues: BuildingGenerationIssue[];
} {
  const { levels, floors, foundationHorizontalAssemblies, idToParents, componentInstances } = input;
  const levelsByWorldKey = groupByWorldKey(levels, (id) =>
    worldKeyForBuildingEntity(id, idToParents, componentInstances),
  );
  const floorsByWorldKey = groupByWorldKey(floors, (_, floor) =>
    worldKeyForBuildingEntity(floor.parentId, idToParents, componentInstances),
  );
  const foundationHorizontalAssembliesByWorldKey = groupByWorldKey(
    foundationHorizontalAssemblies,
    (_, foundationHorizontalAssembly) =>
      worldKeyForBuildingEntity(foundationHorizontalAssembly.levelId, idToParents, componentInstances),
  );

  let allFoundations: Table<GeneratedFoundationSubSystem> = {};
  let allIssues: BuildingGenerationIssue[] = [];
  for (const worldKey in levelsByWorldKey) {
    const { foundations, issues } = generate({
      ...input,
      levels: levelsByWorldKey[worldKey] ?? {},
      floors: floorsByWorldKey[worldKey] ?? {},
      foundationHorizontalAssemblies: foundationHorizontalAssembliesByWorldKey[worldKey] ?? {},
    });
    allFoundations = { ...allFoundations, ...foundations };
    allIssues = [...allIssues, ...issues];
  }
  return { foundations: allFoundations, issues: allIssues };
}

function generate(input: ReturnType<typeof select>): {
  foundations: Table<GeneratedFoundationSubSystem>;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];

  if (Object.values(input.levels).length === 0) {
    return { foundations: {}, issues };
  }

  const foundationsFromAutoGenerated = generateFoundationSystemFromLevels({
    ...input,
    issues,
  });

  const foundationsFromFloors = generateFoundationFromFloors({
    ...input,
    issues,
  });

  // Convert FOUNDATION_SYSTEM_HORIZONTAL_ASSEMBLY entities to FOUNDATION_SYSTEM for overlap resolution.
  // We cannot fully switch horizontal assemblies to deferred extrusion/finalization at this stage because
  // generateWalls depends on foundationHorizontalAssemblies, while room-based foundation generation depends
  // on walls. This stage still needs to resolve overlaps between horizontal assemblies and room-based
  // foundations, so we convert assemblies into intermediate profiles here, run overlap resolution across the
  // merged set, and then rebuild final foundations for downstream consumers.

  // Walls don't need horizontal foundation overlaps resolved, because they only care about the vertical profile of the foundation.
  const convertedHorizontalAssemblies: RWTable<GeneratedIntermediateFoundationSubSystem> = {};
  for (const id in input.foundationHorizontalAssemblies ?? {}) {
    const entity = input.foundationHorizontalAssemblies[id];
    convertedHorizontalAssemblies[id] = {
      ...entity,
      entityType: "FOUNDATION_SYSTEM",
      subSystem: convertFoundationSubSystemToIntermediate(entity.subSystem),
    };
  }

  // Merge all intermediate foundations - foundations from floors take precedence
  const allFoundations: RWTable<GeneratedIntermediateFoundationSubSystem> = {
    ...foundationsFromAutoGenerated,
    ...foundationsFromFloors,
    ...convertedHorizontalAssemblies,
  };

  const resolvedFoundations = applyFoundationOverlapResolution(allFoundations);
  const finalized: RWTable<GeneratedFoundationSubSystem> = {};
  for (const [id, foundation] of Object.entries(resolvedFoundations)) {
    finalized[id] = {
      ...foundation,
      subSystem: buildFoundationSubSystemExtrusions(foundation.subSystem),
    };
  }

  return {
    foundations: finalized,
    issues,
  };
}

/**
 * Generates foundation systems from auto-generated foundation paths (legacy implementation).
 * This processes foundation paths keyed by levelId.
 */
function generateFoundationSystemFromLevels(
  input: ReturnType<typeof select> & { issues: BuildingGenerationIssue[] },
): RWTable<GeneratedIntermediateFoundationSubSystem> {
  const {
    levels,
    planarGraphs,
    foundationPaths,
    floorPaths,
    framingComponents,
    slabComponents,
    sheathingComponents,
    foundationSettings,
    foundationComponents,
    datumAbstractions,
    doorSettings,
    issues,
    rooms,
    roomsByLevel,
  } = input;

  const foundationSettingsFromConfig = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSettingsFromConfig,
  );

  const foundationSystem: RWTable<GeneratedIntermediateFoundationSubSystem> = {};

  const foundationLevelIds = getFoundationLevelIds(levels);

  for (const levelId of foundationLevelIds) {
    const levelGraph = planarGraphs[levelId];
    const level = levels[levelId];

    if (!levels[levelId]?.enableAutoGeneratedFoundation || Object.keys(foundationPaths[levelId] ?? {}).length === 0) {
      continue;
    }

    const levelFoundationPaths = foundationPaths[levelId];
    const levelFloorPaths = floorPaths[levelId];

    if (!levelFoundationPaths) {
      throw new Error(`Foundation paths not found for level ${levelId}`);
    }

    if (!levelFloorPaths) {
      throw new Error(`Floor paths not found for level ${levelId}`);
    }

    const entityHandleForSelectingReportingUnit = entityHandleForSelectingReportingUnitFromLevel(
      levelId,
      rooms,
      roomsByLevel,
    );

    const foundationSubSystem = makeIntermediateFoundationSubSystem(levelFoundationPaths, {
      level,
      planarGraph: levelGraph,
      planarGraphs,
      doorSettings,
      datumAbstractions,
      slabSettings: foundationSettingsFromConfig,
      foundationSubSystemType: foundationSettingsFromConfig.foundationSubSystemType,
      foundationComponents: finalizedFoundationComponents,
      framingComponents,
      slabComponents,
      sheathingComponents,
      issues,
      floorPaths: levelFloorPaths,
      entityHandleForSelectingReportingUnit,
    });

    const foundationId = uid();
    foundationSystem[foundationId] = {
      ...buildingEntityBaseNew("FOUNDATION_SYSTEM", foundationId, entityHandleForSelectingReportingUnit),
      levelId,
      planarGraphId: levelId,
      settings: foundationSettingsFromConfig,
      subSystem: foundationSubSystem,
    };
  }

  return foundationSystem;
}

/**
 * Generates foundation systems from floor horizontal assemblies.
 * This implementation processes Floor entities and generates foundations keyed by FloorId.
 */
function generateFoundationFromFloors(
  input: ReturnType<typeof select> & { issues: BuildingGenerationIssue[] },
): RWTable<GeneratedIntermediateFoundationSubSystem> {
  const {
    levels,
    foundationComponents,
    foundationHorizontalAssembliesPaths,
    floorBoundaries,
    floorSystems,
    floors,
    datumAbstractions,
    doorSettings,
    issues,
    foundationSettings,
    optionChoices,
    entityDisabledByGroup,
    planarGraphs,
    framingComponents,
    slabComponents,
    sheathingComponents,
  } = input;

  const foundationSettingsFromConfig = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSettingsFromConfig,
  );

  const foundationSystem: RWTable<GeneratedIntermediateFoundationSubSystem> = {};

  const firstLevel = bottomLevel(levels);
  if (!firstLevel) {
    throw new Error(`First level not found`);
  }

  const resolvedFloorSystems = evalFormulas(floorSystems, (v) => optionChoices[v], undefined, entityDisabledByGroup);
  const resolvedFloors = evalFormulas(floors, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const floorId in resolvedFloors) {
    const floor = resolvedFloors[floorId];
    const floorSystemId = floor.parentId;
    const disableFoundationAssembly = !!floor.disableFoundationAssembly;

    if (
      !shouldProcessFloorWithBoundaries(floor, floorSystemId, resolvedFloorSystems, floorBoundaries) ||
      disableFoundationAssembly
    ) {
      continue;
    }

    /**
     * @note there is some redundancy here because floorGraph should be availble via paths->planarGraphId in the makeFoundationSubSystem function.
     * WE ARE PASSING IN THE POLYGON PLANAR GRAPH TABLE IN THAT FUNCTION.
     */
    const floorGraph = planarGraphs[floorId];
    if (!floorGraph) {
      console.error(`Floor graph not found for floor ${floorId}`);
      continue;
    }

    const foundationPaths = foundationHorizontalAssembliesPaths[floorId];
    if (!foundationPaths) {
      console.error(`Foundation paths not found for floor ${floorId}`);
      continue;
    }

    /**
     *
     * @note We need to discuss the settings part of this implementation.
     * The previous step defaults to the foundationAssembly from the foundationSettings in the absence of a foundationAssemblyOverride->foundationAssembly.
     * It is also defaulting to the system constraints in the absence of foundationAssemblyOverride->datums.
     *       ? Should we use the defaults from the foundation settings also. if true then we need to know the type of
     *         the foundation super system here as well because the way our settings are organized, they are a function of
     *         the foundation super system type and foundation sub system type.
     */
    const levelToUse =
      "levelId" in floor && floor.levelId && levels[floor.levelId] ? levels[floor.levelId] : firstLevel;
    const levelIdToUse = levelToUse.id;

    const floorSelectionHandle = entityHandleForSelectingReportingUnitFromEntityId(floorId, {
      type: "FLOOR",
      id: floorId,
    });

    const foundationSubSystem = makeIntermediateFoundationSubSystem(foundationPaths, {
      level: levelToUse,
      planarGraph: floorGraph,
      planarGraphs,
      doorSettings,
      datumAbstractions,
      foundationSubSystemType: foundationSettingsFromConfig.foundationSubSystemType,
      foundationComponents: finalizedFoundationComponents,
      framingComponents,
      slabComponents,
      sheathingComponents,
      issues,
      entityHandleForSelectingReportingUnit: floorSelectionHandle,
    });

    foundationSystem[floorId] = {
      ...buildingEntityBaseNew("FOUNDATION_SYSTEM", floorId, floorSelectionHandle),
      levelId: levelIdToUse,
      planarGraphId: floorId,
      settings: foundationSettingsFromConfig,
      subSystem: foundationSubSystem,
    } as GeneratedIntermediateFoundationSubSystem;
  }

  return foundationSystem;
}

export default memo(select, generateFoundationSystemsForWorlds, compare);
